import React, { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAbility } from '@casl/react';
import { captureException } from '@sentry/react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { decodeJWT, getJWT } from '@bynder/js-access-token-lib';

import { Can, AbilityContext } from '../../abilities/Can';
import { isAccessTokenRetrievalError } from '../../helpers/isAccessTokenRetrievalError';

import { AccountSwitcher } from '../../components/AccountSwitcher/AccountSwitcher';
import { Brandstore } from '../../components/Brandstore/Brandstore';
import { rootHeaderTopBar } from '../../roots';
import { InfoContext } from '../../context';

import { HybridNotificationCenter } from '../../components/Notifications/HybridNotificationCenter';
import {
	isModule,
	isColdfusion,
	isPublicOrShareCollectionView,
} from '../../helpers/windowLocation';
import { FadeIn } from '../../components/FadeIn';
import Indicator from '../../components/OutOfOffice/indicator';
import ReplacementPortal from './ReplacementPortal';
import { ConversationalAI } from '../../components/ConversationalAI/ConversationalAI';
import { UserMenu } from '../../components/UserMenu/UserMenu';
import { AccountMenu } from '../../components/AccountMenu/AccountMenu';
import { PublicMenu } from '../../components/PublicMenu/PublicMenu';
import { Spotlight } from '../../components/Spotlight/Spotlight';
import { useSplit } from '../../helpers/useSplit';
import { BREAKPOINTS } from '../../breakpoints';
import { SEARCH_BAR_HEIGHT_CLOSED } from '../../components/Spotlight/redesign/constants';

/*
 * bynder-platform/bynder/views/navigation/topmenu.cfm
 */

function Component() {
	const ability = useAbility(AbilityContext);
	const { accounts, general } = useContext(InfoContext);
	const [isJwtImpersonating, setIsJwtImpersonating] = useState<boolean | null>(null);
	const isSpotlightRedesignEnabled = useSplit('discover_spotlight_redesign_phase_0');

	const canShowAccSwitcher = ability.can('show', 'account_switcher');
	const dashboardIsBeingRendered = window.document
		.querySelector('html')
		?.classList.contains('dashboard');
	const isDashboardSearchOn = dashboardIsBeingRendered && ability.can('show', 'dashboard_search');

	useEffect(() => {
		const checkJwtImpersonation = async () => {
			try {
				const jwt = await getJWT();

				if (jwt) {
					const decoded = decodeJWT(jwt);
					const hasImpersonatorClaim = !!decoded?.impersonatorUserId;
					setIsJwtImpersonating(hasImpersonatorClaim);
				}
			} catch (error) {
				setIsJwtImpersonating(false);

				if (!isAccessTokenRetrievalError(error)) {
					captureException(error);
				}
			}
		};
		checkJwtImpersonation();
	}, []);

	// Don't show account switcher when user is impersonating (new impersonate banner is handled elsewhere)
	const showAccountSwitcher = !isJwtImpersonating && (canShowAccSwitcher || accounts.length > 0);
	// Search needs a session, so logged-out users only get Spotlight on public collection pages.
	const canUseSpotlight = general.isLoggedIn || isPublicOrShareCollectionView();
	const showOnGuidelines = isModule('guidelines') && !isDashboardSearchOn;
	const showSpotlight =
		canUseSpotlight &&
		((!isModule('templating') && !isDashboardSearchOn && isColdfusion()) || showOnGuidelines);

	// Temporary code for replacing the coldfusion spotlight
	// so that we can remove the legacy code
	const rootNode = document.querySelector('#rootDashboardSpotlight');

	// The redesigned Spotlight needs more room, so it gets its own row from tablet width already (instead of from mobile width, like the old Spotlight).
	const searchOwnRowMaxWidth = isSpotlightRedesignEnabled
		? BREAKPOINTS.TABLET
		: BREAKPOINTS.MOBILE;

	const replaceSpotlight =
		canUseSpotlight &&
		((!isModule('templating') && isDashboardSearchOn && isColdfusion()) || showOnGuidelines);

	if (ability.rules.length === 0) {
		return null;
	}

	return (
		<>
			{showAccountSwitcher && (
				<AccountSwitcherWrapper>
					<AccountSwitcher />
				</AccountSwitcherWrapper>
			)}

			{showSpotlight && (
				<>
					<SearchRowStyle $ownRowMaxWidth={searchOwnRowMaxWidth} />
					<SearchWrapper
						$ownRowMaxWidth={searchOwnRowMaxWidth}
						$isRedesign={Boolean(isSpotlightRedesignEnabled)}
					>
						<Spotlight isSpotlightRedesignEnabled={isSpotlightRedesignEnabled} />
					</SearchWrapper>
				</>
			)}

			{replaceSpotlight && rootNode && (
				<ReplacementPortal rootNode={rootNode}>
					<Spotlight isSpotlightRedesignEnabled={isSpotlightRedesignEnabled} />
				</ReplacementPortal>
			)}

			<MobileNav id="mobile-nav" />

			<div className="admin-right">
				{general.isPublic ? (
					<PublicMenu />
				) : (
					<>
						<Can I="show" a="notifications">
							<ul className="badges">
								<li>
									<Indicator />
								</li>

								<Can I="show" a="brandstore">
									<Brandstore />
								</Can>

								<ConversationalAI />

								<HybridNotificationCenter />
							</ul>
						</Can>

						<ul
							className="admin-options"
							style={{ display: 'flex', alignItems: 'center' }}
						>
							<UserMenu />
							<AccountMenu />
						</ul>
					</>
				)}
			</div>
		</>
	);
}

export function TopBar() {
	const root = document.querySelector(rootHeaderTopBar);

	if (root) {
		return createPortal(
			<FadeIn>
				<Wrapper>
					<Component />
				</Wrapper>
			</FadeIn>,
			root,
		);
	}

	return null;
}

const Wrapper = styled.div`
	@media (max-width: ${BREAKPOINTS.TABLET}px) {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		align-items: center;

		& > .admin-right {
			margin-left: auto;
		}
	}
`;

const AccountSwitcherWrapper = styled.div`
	@media (max-width: ${BREAKPOINTS.TABLET}px) {
		display: none;
	}
`;

const SEARCH_ROW_PADDING_BOTTOM = 8;

// !important used to override header-admin.css and bynder-themes. The open redesigned card
// grows in height, so on its own row it's taken out of flow or the header would grow with it.
const SearchWrapper = styled.div<{ $ownRowMaxWidth: number; $isRedesign: boolean }>`
	flex: 1;
	min-width: 0;

	@media (max-width: ${BREAKPOINTS.TABLET}px) {
		& .nav-bar-search {
			position: relative !important;
		}

		& .nav-bar-search input.search {
			width: 100% !important;
		}
	}

	@media (max-width: ${({ $ownRowMaxWidth }) => $ownRowMaxWidth}px) {
		order: 1;
		flex-basis: 100%;
		padding-bottom: ${SEARCH_ROW_PADDING_BOTTOM}px;

		& .nav-bar-search {
			max-width: none;
		}
	}

	${({ $isRedesign }) =>
		$isRedesign &&
		css`
			@media (max-width: ${BREAKPOINTS.TABLET}px) {
				position: relative;
				height: ${SEARCH_BAR_HEIGHT_CLOSED + SEARCH_ROW_PADDING_BOTTOM}px;

				& .nav-bar-search {
					position: absolute !important;
					top: 0 !important;
					left: 0 !important;
					right: 0 !important;
				}
			}
		`}
`;

// bynder-themes fix the admin-bar's height, so the wrapped search row would spill out of it.
const SearchRowStyle = createGlobalStyle<{ $ownRowMaxWidth: number }>`
	@media (max-width: ${({ $ownRowMaxWidth }) => $ownRowMaxWidth}px) {
		header > #rootHeaderTopBar {
			height: auto !important;
		}
	}
`;

const MobileNav = styled.div`
	display: none;

	@media (max-width: ${BREAKPOINTS.MOBILE}px) {
		display: block;
	}
`;
